// data-lf-particles, data-lf-scramble, data-lf-draw, data-lf-burst, data-lf-velocity,
// data-lf-drag, data-lf-tilt-scroll, data-lf-cursor-glow, data-lf-typewriter.
if (!window.__lfFxReady) {
  window.__lfFxReady = true;
  const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const fine = window.matchMedia?.('(hover:hover) and (pointer:fine)');
  const bindings = new WeakMap(), cleanups = new Map(), jobs = new Set(), animations = new Set();
  const clamp = (n, min = 0, max = 1) => Math.min(max, Math.max(min, n));
  const number = (v, fallback) => v != null && v !== '' && Number.isFinite(Number(v)) ? Number(v) : fallback;
  const css = (el, key, value) => el.style.setProperty(`--lf-${key}`, value);
  const reduced = () => motion?.matches ?? true;
  const colour = (el, key, fallback) => getComputedStyle(el).getPropertyValue(`--lf-${key}`).trim() || fallback;
  let frame = 0, last = 0, dirty = true, overlay;
  function wake() {
    dirty = true;
    if (!frame && !document.hidden) frame = window.requestAnimationFrame?.(tick) || 0;
  }
  function tick(time) {
    frame = 0;
    if (document.hidden) { last = 0; return; }
    const dt = last ? Math.min(time - last, 48) : 16;
    last = time;
    const changed = dirty, writes = [];
    dirty = false;
    for (const job of jobs) {
      if (!job.el.isConnected) continue;
      if (!window.IntersectionObserver) {
        const r = job.el.getBoundingClientRect();
        const area = Math.max(0, Math.min(r.bottom, innerHeight) - Math.max(r.top, 0)) * Math.max(0, Math.min(r.right, innerWidth) - Math.max(r.left, 0));
        job.visible = area > 0 && area / Math.max(1, r.width * r.height) >= job.threshold;
      }
      if (job.visible && (changed || job.active)) {
        const write = job.read(dt);
        if (write) writes.push(write);
      }
    }
    writes.forEach(write => write());
    if ([...jobs].some(job => job.visible && job.active && job.el.isConnected) || dirty) {
      if (!frame) frame = window.requestAnimationFrame?.(tick) || 0;
    } else last = 0;
  }
  function bind(el, name, setup) {
    let names = bindings.get(el);
    if (!names) bindings.set(el, names = new Set());
    if (names.has(name)) return;
    names.add(name);
    const disposers = [], add = fn => disposers.push(fn);
    const on = (target, type, fn, options = { passive: true }) => {
      target.addEventListener(type, fn, options);
      add(() => target.removeEventListener(type, fn, options));
    };
    if (!cleanups.has(el)) cleanups.set(el, new Set());
    cleanups.get(el).add(() => { disposers.reverse().forEach(fn => fn()); names.delete(name); });
    setup(el, add, on);
  }
  function jobFor(el, add, read, active = false, threshold = 0) {
    const job = { el, read, active, threshold, visible: !window.IntersectionObserver };
    jobs.add(job);
    if (window.IntersectionObserver) {
      const observer = new IntersectionObserver(entries => {
        job.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= threshold;
        wake();
      }, { threshold });
      observer.observe(el);
      add(() => observer.disconnect());
    }
    add(() => jobs.delete(job));
    return job;
  }
  function resized(el, add, on, fn = wake, children = false) {
    if (window.ResizeObserver) {
      const observer = new ResizeObserver(fn);
      observer.observe(el);
      if (children) [...el.children].forEach(child => observer.observe(child));
      add(() => observer.disconnect());
    }
    on(window, 'resize', fn);
  }
  function styles(el, keys, add) {
    const saved = keys.map(key => [key, el.style.getPropertyValue(key), el.style.getPropertyPriority(key)]);
    add(() => saved.forEach(([key, value, priority]) => value ? el.style.setProperty(key, value, priority) : el.style.removeProperty(key)));
  }
  function animator(add) {
    const owned = new Set();
    add(() => owned.forEach(animation => { animation.onfinish = null; animation.cancel(); animations.delete(animation); }));
    return (node, frames, options, done = () => {}) => {
      if (!node.animate) { done(); return; }
      let animation;
      try { animation = node.animate(frames, options); } catch { done(); return; }
      owned.add(animation); animations.add(animation);
      animation.onfinish = () => { owned.delete(animation); animations.delete(animation); done(); animation.cancel(); };
      if (document.hidden) animation.pause();
    };
  }
  function particles(el, add, on) {
    if (reduced()) return;
    const canvas = document.createElement('canvas');
    let ctx;
    try { ctx = canvas.getContext?.('2d'); } catch { return; }
    if (!ctx) return;
    canvas.className = 'lf-particles'; canvas.setAttribute('aria-hidden', 'true');
    canvas.style.width = '100%'; canvas.style.height = '100%';
    el.prepend(canvas); add(() => canvas.remove());
    const sprite = document.createElement('canvas'); sprite.width = sprite.height = 32;
    const pen = sprite.getContext('2d');
    if (!pen) { canvas.remove(); return; }
    const gradient = pen.createRadialGradient(16, 16, 0, 16, 16, 16);
    gradient.addColorStop(0, colour(el, 'accent', '#c8f02c')); gradient.addColorStop(1, 'transparent');
    pen.fillStyle = gradient; pen.fillRect(0, 0, 32, 32);
    on(document, 'shopify:section:load', e => {
      if (e.target === el || e.target.contains(el)) {
        const tint = pen.createRadialGradient(16, 16, 0, 16, 16, 16);
        tint.addColorStop(0, colour(el, 'accent', '#c8f02c')); tint.addColorStop(1, 'transparent');
        pen.clearRect(0, 0, 32, 32); pen.fillStyle = tint; pen.fillRect(0, 0, 32, 32);
      }
    });
    const stars = el.dataset.lfParticles === 'stars';
    let width = 0, height = 0, ratio = 0, points = [], pointer, ox = 0, oy = 0, age = 0, next = 6000 + Math.random() * 4000, streak;
    const job = jobFor(el, add, dt => {
      const rect = el.getBoundingClientRect(), dpr = Math.min(window.devicePixelRatio || 1, 2);
      const count = Math.round(clamp(number(el.dataset.lfParticlesCount, 46), 0, 300) * (innerWidth < 750 ? 0.5 : 1));
      const resize = width !== rect.width || height !== rect.height || ratio !== dpr || count !== points.length;
      width = rect.width; height = rect.height; ratio = dpr;
      if (resize) points = Array.from({ length: count }, () => ({ x: Math.random() * width, y: Math.random() * height, r: 1 + Math.random() * 2.2, phase: Math.random() * 6.28, a: 0.15 + Math.random() * 0.55 }));
      const tx = pointer && fine?.matches ? clamp((pointer.x - rect.left) / (width || 1) - 0.5, -0.5, 0.5) * 36 : 0;
      const ty = pointer && fine?.matches ? clamp((pointer.y - rect.top) / (height || 1) - 0.5, -0.5, 0.5) * 36 : 0;
      ox += (tx - ox) * 0.06; oy += (ty - oy) * 0.06; age += dt;
      if (stars && age >= next) { streak = { x: Math.random() * width, y: Math.random() * height * 0.5, age: 0 }; next = age + 6000 + Math.random() * 4000; }
      return () => {
        if (resize) { canvas.width = Math.round(width * ratio); canvas.height = Math.round(height * ratio); ctx.setTransform(ratio, 0, 0, ratio, 0, 0); }
        ctx.clearRect(0, 0, width, height);
        for (const p of points) {
          if (!stars) p.y = (p.y - dt * 0.008 + height + 12) % (height + 12);
          ctx.globalAlpha = clamp(p.a * (0.8 + Math.sin(age / 900 + p.phase) * 0.2), 0.15, 0.7);
          ctx.drawImage(sprite, p.x + ox + (stars ? 0 : Math.sin(age / 1800 + p.phase) * 8) - p.r, p.y + oy - p.r, p.r * 2, p.r * 2);
        }
        if (streak) {
          streak.age += dt;
          ctx.globalAlpha = Math.max(0, 1 - streak.age / 500);
          for (let i = 0; i < 12; i++) ctx.drawImage(sprite, streak.x + streak.age * 0.3 - i * 3, streak.y + streak.age * 0.12 - i, 3, 3);
          if (streak.age >= 500) streak = null;
        }
      };
    }, true);
    on(el, 'pointermove', e => { if (fine?.matches && e.pointerType !== 'touch' && job.visible) pointer = { x: e.clientX, y: e.clientY }; });
    on(el, 'pointerleave', () => { pointer = null; }); resized(el, add, on);
  }
  function textEffect(el, add, on, typing = false) {
    if (reduced() || ![...el.childNodes].every(node => node.nodeType === 3)) return;
    const text = el.textContent, chars = Array.from(text), glyphs = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789·/+';
    if (!chars.length) return;
    let started = false, done = false, elapsed = 0, copy, caret, rendered;
    const duration = typing ? chars.length * Math.max(1, number(el.dataset.lfTypewriterSpeed, 34)) : Math.max(1, number(el.dataset.lfScrambleDuration, 650));
    styles(el, ['min-height', 'display', 'width'], add);
    const restore = () => el.replaceChildren(document.createTextNode(text));
    add(() => { if (started) restore(); });
    const job = jobFor(el, add, dt => {
      if (done) return;
      const rect = !started ? el.getBoundingClientRect() : null;
      const inline = !started && getComputedStyle(el).display === 'inline';
      elapsed += started ? dt : 0;
      return () => {
        if (!started) {
          started = true;
          const sr = document.createElement('span'); sr.className = 'lf-sr'; sr.textContent = text;
          copy = document.createElement('span'); copy.className = typing ? 'lf-typewriter' : 'lf-scramble'; copy.setAttribute('aria-hidden', 'true');
          if (typing) {
            if (inline) { el.style.display = 'inline-block'; el.style.width = `${rect.width}px`; }
            el.style.minHeight = `${Math.max(rect.height, parseFloat(el.style.minHeight) || 0)}px`;
            caret = document.createElement('span'); caret.className = 'lf-caret'; caret.setAttribute('aria-hidden', 'true');
            caret.style.position = 'absolute';
          } else { copy.style.display = 'inline-block'; copy.style.width = `${rect.width}px`; }
          el.replaceChildren(sr, copy);
        }
        const n = Math.floor(clamp(elapsed / duration) * chars.length);
        const value = typing ? chars.slice(0, n).join('') : chars.map((c, i) => /\s/.test(c) || i < n ? c : glyphs[Math.floor(Math.random() * glyphs.length)]).join('');
        if (value !== rendered) { copy.textContent = value; rendered = value; }
        if (typing) { caret.style.opacity = String(Math.floor(elapsed / 500) % 2 ? 0 : 1); if (!caret.parentNode) copy.append(caret); }
        if (elapsed >= duration + (typing ? 1200 : 0)) { restore(); done = true; job.active = false; }
        else job.active = true;
      };
    }, false, typing ? 0.3 : 0.4);
  }
  function draw(el, add) {
    if (reduced()) return;
    const paths = [...el.querySelectorAll('path, line, polyline, polygon, circle, ellipse, rect')]
      .filter(path => getComputedStyle(path).stroke !== 'none' && path.animate);
    const lengths = paths.map(path => { try { return path.getTotalLength?.() || 300; } catch { return 300; } });
    paths.forEach((path, i) => {
      styles(path, ['stroke-dasharray', 'stroke-dashoffset'], add);
      path.style.strokeDasharray = lengths[i]; path.style.strokeDashoffset = lengths[i];
    });
    const animate = animator(add);
    let started = false;
    jobFor(el, add, () => {
      if (started) return;
      started = true;
      return () => paths.forEach((path, i) => animate(path, [{ strokeDashoffset: lengths[i] }, { strokeDashoffset: 0 }], {
        duration: Math.max(0, number(el.dataset.lfDrawDuration, 900)), delay: i * Math.max(0, number(el.dataset.lfDrawStagger, 120)), easing: 'cubic-bezier(.65,0,.35,1)', fill: 'forwards',
      }, () => { path.style.removeProperty('stroke-dasharray'); path.style.removeProperty('stroke-dashoffset'); }));
    }, false, 0.3);
  }
  function burst(el, add, on) {
    if (reduced() || !el.animate) return;
    const animate = animator(add), nodes = new Set();
    let point;
    const remove = node => { node.remove(); nodes.delete(node); if (overlay && !overlay.childElementCount) { overlay.remove(); overlay = null; } };
    add(() => [...nodes].forEach(remove));
    on(el, 'pointerdown', e => { point = { x: e.clientX, y: e.clientY, time: performance.now() }; });
    on(el, 'pointercancel', () => { point = null; });
    on(el, 'click', e => {
      const rect = el.getBoundingClientRect(), accent = colour(el, 'accent', '#c8f02c'), ink = colour(el, 'ink', '#1c1f1d');
      const p = e.detail && point && performance.now() - point.time < 1500 ? point : { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
      point = null;
      if (!overlay) {
        overlay = document.createElement('div'); overlay.setAttribute('aria-hidden', 'true');
        overlay.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:2147483000;overflow:hidden'; document.body.append(overlay);
      }
      const count = Math.round((12 + Math.random() * 4) * (innerWidth < 750 ? 0.5 : 1));
      for (let i = 0; i < count; i++) {
        const node = document.createElement('span'), angle = Math.random() * Math.PI * 2, distance = 40 + Math.random() * 50;
        const x = Math.cos(angle) * distance, y = Math.sin(angle) * distance, rotation = Math.random() * 360;
        node.style.cssText = `position:absolute;left:${p.x}px;top:${p.y}px;width:${i % 2 ? 10 : 6}px;height:${i % 2 ? 3 : 6}px;border-radius:9px;background:${i % 3 ? accent : ink}`;
        nodes.add(node); overlay.append(node);
        animate(node, [{ transform: 'translate(-50%,-50%) scale(1)', opacity: 1 }, { transform: `translate(${x * 0.6}px,${y * 0.6}px) rotate(${rotation * 0.6}deg)`, opacity: 0.8, offset: 0.6 }, { transform: `translate(${x}px,${y + 25}px) rotate(${rotation}deg) scale(.3)`, opacity: 0 }], { duration: 550 + Math.random() * 200, easing: 'ease-out' }, () => remove(node));
      }
      const ring = document.createElement('span'), size = Math.max(rect.width, rect.height);
      ring.className = 'lf-ripple'; ring.setAttribute('aria-hidden', 'true');
      ring.style.cssText = `left:${p.x - rect.left - size / 2}px;top:${p.y - rect.top - size / 2}px;width:${size}px;height:${size}px;border:1px solid ${accent};pointer-events:none`;
      nodes.add(ring); el.append(ring);
      animate(ring, [{ transform: 'scale(0)', opacity: 0.6 }, { transform: 'scale(2.4)', opacity: 0 }], { duration: 650, easing: 'ease-out' }, () => remove(ring));
    });
  }
  function velocity(el, add) {
    if (reduced()) return;
    styles(el, ['--lf-skew', '--lf-velocity-scale'], add);
    let previous = window.scrollY, speed = 0, skew = 0, derivative = 0, written = false;
    const strength = number(el.dataset.lfVelocity, 4);
    const job = jobFor(el, add, dt => {
      const y = window.scrollY, v = clamp((y - previous) / dt, -5, 5); previous = y;
      speed += (v - speed) * (1 - Math.exp(-dt / 45));
      const target = clamp(speed * strength, -7, 7), seconds = dt / 1000, omega = 24;
      const offset = skew - target, c = derivative + omega * offset, decay = Math.exp(-omega * seconds);
      skew = target + (offset + c * seconds) * decay; derivative = (derivative - omega * c * seconds) * decay;
      const moving = Math.abs(skew) > 0.005 || Math.abs(speed) > 0.005 || Math.abs(derivative) > 0.01;
      const settled = written && !job.active && !moving;
      job.active = moving;
      if (!moving) { skew = 0; derivative = 0; speed = 0; }
      if (settled) return;
      written = true;
      return () => { css(el, 'skew', `${clamp(skew, -7, 7).toFixed(3)}deg`); css(el, 'velocity-scale', 1 + Math.abs(skew) / 60); };
    });
  }
  function drag(el, add, on) {
    styles(el, ['scroll-snap-type', '--lf-scroll-progress', 'scroll-behavior', 'user-select'], add);
    const snap = el.style.getPropertyValue('scroll-snap-type'), priority = el.style.getPropertyPriority('scroll-snap-type'), behavior = el.style.scrollBehavior;
    const selection = el.style.userSelect;
    const classes = ['is-dragging', 'is-at-start', 'is-at-end'].map(name => [name, el.classList.contains(name)]);
    add(() => classes.forEach(([name, value]) => el.classList.toggle(name, value)));
    let down, pending, inertia = 0, suppress = false, suppressUntil = 0, dots, children = [], rebuild = true;
    const reset = () => { down = null; pending = null; inertia = 0; el.classList.remove('is-dragging'); snap ? el.style.setProperty('scroll-snap-type', snap, priority) : el.style.removeProperty('scroll-snap-type'); el.style.scrollBehavior = behavior; el.style.userSelect = selection; };
    add(() => { reset(); dots?.remove(); });
    const job = jobFor(el, add, dt => {
      const max = Math.max(0, el.scrollWidth - el.clientWidth), left = el.scrollLeft, rect = el.getBoundingClientRect();
      const rtl = getComputedStyle(el).direction === 'rtl', sign = rtl ? -1 : 1;
      if (rebuild) children = [...el.children];
      const positions = children.map(child => { const r = child.getBoundingClientRect(); return rtl ? rect.right - r.right : r.left - rect.left; });
      const index = max > 1 && Math.abs(left) >= max - 1 ? children.length - 1 : positions.reduce((best, value, i) => Math.abs(value) < Math.abs(positions[best]) ? i : best, 0);
      const progress = max ? clamp(Math.abs(left) / max) : 0;
      let next = pending;
      if (!down && Math.abs(inertia) > 0.02) { next = clamp((left + inertia * dt) * sign, 0, max) * sign; inertia *= Math.exp(-dt / 180); if (next === left) inertia = 0; }
      job.active = !down && Math.abs(inertia) > 0.02;
      return () => {
        if (rebuild) {
          rebuild = false; dots?.removeEventListener('click', dotClick); dots?.remove(); dots = null;
          if (el.hasAttribute('data-lf-drag-dots') && max > 1 && children.length > 1) {
            dots = document.createElement('div'); dots.className = 'lf-dots'; dots.setAttribute('role', 'group'); dots.setAttribute('aria-label', 'Slides');
            children.forEach((child, i) => { const button = document.createElement('button'); button.type = 'button'; button.className = 'lf-dot'; button.setAttribute('aria-label', `Go to slide ${i + 1}`); button.dataset.index = i; dots.append(button); });
            dots.addEventListener('click', dotClick); el.after(dots);
          }
        }
        if (next != null) { el.scrollLeft = next; pending = null; }
        if (!down && !job.active) reset();
        css(el, 'scroll-progress', progress); el.classList.toggle('is-at-start', progress <= 0.001); el.classList.toggle('is-at-end', max <= 1 || progress >= 0.999);
        [...(dots?.children || [])].forEach((button, i) => i === index ? button.setAttribute('aria-current', 'true') : button.removeAttribute('aria-current'));
      };
    });
    function dotClick(e) {
      const button = e.target.closest('button[data-index]');
      if (button) children[Number(button.dataset.index)]?.scrollIntoView?.({ inline: 'start', block: 'nearest', behavior: reduced() ? 'instant' : 'smooth' });
    }
    on(el, 'scroll', wake); resized(el, add, on, () => { rebuild = true; wake(); }, true);
    on(el, 'pointerdown', e => {
      if (!fine?.matches || e.pointerType !== 'mouse' || e.button !== 0) return;
      reset(); suppress = false;
      down = { id: e.pointerId, x: e.clientX, left: el.scrollLeft, lastX: e.clientX, time: performance.now() };
      el.style.userSelect = 'none';
    });
    on(window, 'pointermove', e => {
      if (!down || e.pointerId !== down.id) return;
      const now = performance.now(), distance = e.clientX - down.x;
      if (Math.abs(distance) > 6) suppress = true;
      inertia = (down.lastX - e.clientX) / Math.max(1, now - down.time); down.lastX = e.clientX; down.time = now;
      if (suppress) { el.style.setProperty('scroll-snap-type', 'none'); el.style.scrollBehavior = 'auto'; el.classList.add('is-dragging'); pending = down.left - distance; wake(); }
    });
    const release = e => {
      if (!down || e.pointerId !== down.id) return;
      if (!suppress || performance.now() - down.time > 80 || reduced()) inertia = 0;
      down = null; suppressUntil = performance.now() + 400; el.classList.remove('is-dragging'); job.active = Math.abs(inertia) > 0.02; wake();
    };
    on(window, 'pointerup', release); on(window, 'pointercancel', () => { reset(); job.active = false; });
    on(window, 'blur', () => { reset(); job.active = false; });
    on(el, 'dragstart', e => { if (down) e.preventDefault(); }, { capture: true });
    on(el, 'click', e => { if (suppress && performance.now() <= suppressUntil && e.detail) { e.preventDefault(); e.stopImmediatePropagation(); suppress = false; } }, { capture: true });
    on(el, 'wheel', () => { reset(); job.active = false; });
    add(() => dots?.removeEventListener('click', dotClick));
  }
  function tiltScroll(el, add) {
    styles(el, ['--lf-ts'], add);
    if (reduced()) { css(el, 'ts', 1); return; }
    jobFor(el, add, () => {
      const p = clamp((innerHeight - el.getBoundingClientRect().top) / Math.max(1, innerHeight / 2));
      return () => css(el, 'ts', 1 - (1 - p) ** 3);
    });
  }
  function glow(el, add, on) {
    if (reduced() || !fine?.matches) return;
    styles(el, ['--lf-gx', '--lf-gy'], add);
    const original = el.classList.contains('is-glowing'); add(() => el.classList.toggle('is-glowing', original));
    let pointer, x, y;
    const job = jobFor(el, add, () => {
      if (!pointer) { job.active = false; return; }
      const rect = el.getBoundingClientRect(), tx = pointer.x - rect.left, ty = pointer.y - rect.top;
      x = x == null ? tx : x + (tx - x) * 0.15; y = y == null ? ty : y + (ty - y) * 0.15;
      job.active = Math.abs(tx - x) + Math.abs(ty - y) > 0.1;
      return () => { css(el, 'gx', `${x.toFixed(2)}px`); css(el, 'gy', `${y.toFixed(2)}px`); };
    });
    const move = e => { if (fine?.matches && e.pointerType !== 'touch') { pointer = { x: e.clientX, y: e.clientY }; el.classList.add('is-glowing'); wake(); } };
    on(el, 'pointerenter', move); on(el, 'pointermove', move);
    on(el, 'pointerleave', () => { pointer = null; job.active = false; el.classList.remove('is-glowing'); });
  }
  function anatomy(el, add, on) {
    const tabs = [...el.querySelectorAll('[data-lf-anatomy-part]')];
    const hotspots = [...el.querySelectorAll('[data-lf-anatomy-hotspot]')];
    const lines = [...el.querySelectorAll('[data-lf-anatomy-line]')];
    const lens = el.querySelector('[data-lf-anatomy-lens]');
    const contour = el.querySelector('.lf-anatomy-contour path');
    if (!lens) return;
    const animate = animator(add);
    const moving = () => !reduced() && document.documentElement.classList.contains('lf-motion');
    let current, ticket = 0, disposed = false, revealed = false;
    add(() => { disposed = true; ticket++; el.classList.remove('is-ready'); lens.replaceChildren(); lens.removeAttribute('aria-busy'); });
    async function activate() {
      const tab = tabs.find(button => button.getAttribute('aria-selected') === 'true');
      if (!tab || tab === current) return;
      current = tab;
      const part = tab.dataset.lfAnatomyPart, version = ++ticket;
      hotspots.forEach(button => {
        const active = button.dataset.lfAnatomyHotspot === part;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      lines.forEach(path => {
        const active = path.dataset.lfAnatomyLine === part;
        path.toggleAttribute('hidden', !active);
        if (active && moving()) animate(path, [{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }], { duration: 500, easing: 'ease-out' });
      });
      const panel = el.querySelector(`[id="${tab.getAttribute('aria-controls')}"]`);
      const source = panel?.querySelector('[data-lf-anatomy-source]');
      if (!source) return;
      const next = source.cloneNode(true);
      next.removeAttribute('data-lf-anatomy-source');
      next.className = 'lf-anatomy-detail';
      const image = next.querySelector('img');
      lens.setAttribute('aria-busy', 'true');
      if (image) {
        image.loading = 'eager';
        try {
          if (image.decode) await image.decode();
          else if (!image.complete) await new Promise(resolve => {
            const done = () => { image.removeEventListener('load', done); image.removeEventListener('error', done); resolve(); };
            image.addEventListener('load', done); image.addEventListener('error', done);
            add(done);
          });
          if (!image.naturalWidth) throw new Error('Unavailable detail image');
        } catch {
          const placeholder = el.querySelector('.lf-anatomy-placeholder');
          if (placeholder) next.replaceChildren(placeholder.cloneNode(true));
          else {
            const fallback = document.createElement('span');
            fallback.className = 'lf-anatomy-fallback'; fallback.textContent = image.alt;
            next.replaceChildren(fallback);
          }
        }
      }
      if (disposed || ticket !== version) return;
      const previous = lens.lastElementChild;
      lens.append(next);
      el.classList.add('is-ready');
      lens.removeAttribute('aria-busy');
      const finish = () => { if (ticket === version) lens.replaceChildren(next); };
      if (moving()) {
        if (previous) {
          previous.setAttribute('aria-hidden', 'true');
          animate(previous, [{ opacity: 1 }, { opacity: 0 }], { duration: 260, easing: 'ease-out', fill: 'forwards' });
        }
        animate(next, [{ opacity: 0, transform: 'scale(.98)' }, { opacity: 1, transform: 'scale(1)' }], { duration: 260, easing: 'ease-out' }, finish);
      } else finish();
    }
    const observer = new MutationObserver(activate);
    tabs.forEach(tab => observer.observe(tab, { attributes: true, attributeFilter: ['aria-selected'] }));
    add(() => observer.disconnect());
    hotspots.forEach(button => on(button, 'click', () => tabs.find(tab => tab.dataset.lfAnatomyPart === button.dataset.lfAnatomyHotspot)?.click()));
    const list = el.querySelector('[role="tablist"]');
    on(list, 'keydown', event => {
      if (!['ArrowUp', 'ArrowDown'].includes(event.key)) return;
      const index = tabs.indexOf(event.target);
      if (index < 0) return;
      event.preventDefault();
      const next = tabs[(index + (event.key === 'ArrowDown' ? 1 : -1) + tabs.length) % tabs.length];
      next.click(); next.focus({ preventScroll: true });
    }, { passive: false });
    on(list, 'focusin', event => {
      if (innerWidth < 750 && tabs.includes(event.target)) event.target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    });
    if (contour && moving()) {
      styles(contour, ['stroke-dashoffset'], add);
      contour.style.strokeDashoffset = 1;
    }
    if (contour) jobFor(el, add, () => {
      if (revealed) return;
      revealed = true;
      return () => {
        contour.style.strokeDashoffset = 0;
        if (moving()) animate(contour, [{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }], { duration: 1200, easing: 'ease-out' });
      };
    }, false, 0.2);
    activate();
  }
  const effects = { anatomy, particles, scramble: textEffect, draw, burst, velocity, drag, 'tilt-scroll': tiltScroll, 'cursor-glow': glow, typewriter: (el, add, on) => textEffect(el, add, on, true) };
  function init(root = document) {
    for (const [name, setup] of Object.entries(effects)) {
      const selector = `[data-lf-${name}]`;
      [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)]
        .filter(el => !el.closest('[data-lf-marquee-clone]')).forEach(el => bind(el, name, setup));
    }
    wake();
  }
  function dispose(root) {
    for (const [el, disposers] of cleanups) if (el === root || root.contains(el)) { disposers.forEach(fn => fn()); cleanups.delete(el); }
    if (![...jobs].some(job => job.visible && job.active)) { window.cancelAnimationFrame?.(frame); frame = 0; last = 0; }
  }
  window.addEventListener('scroll', wake, { passive: true });
  window.addEventListener('resize', wake, { passive: true });
  document.addEventListener('visibilitychange', () => {
    window.cancelAnimationFrame?.(frame); frame = 0; last = 0;
    animations.forEach(animation => document.hidden ? animation.pause() : animation.play());
    if (!document.hidden) wake();
  });
  document.addEventListener('shopify:section:load', e => init(e.target));
  document.addEventListener('shopify:section:unload', e => dispose(e.target));
  motion?.addEventListener?.('change', () => { dispose(document); init(); });
  init();
}
